/**
 * Central mapping for product CSV lookup IDs to their price comparison URLs
 * This ensures consistent URLs across all components regardless of how the product is accessed
 */

export const CSV_LOOKUP_TO_URL_MAPPING = {
  // ECHOMAP UHD2 Series
  "ECHOMAP UHD2 73sv": "/simple-products/echomap-73sv-uhd2",
  "ECHOMAP UHD2 93sv": "/simple-products/echomap-93sv-uhd2",
  
  // ECHOMAP Ultra 2 Series
  "Echomap Ultra 2 106sv": "/simple-products/echomap-ultra-2-106sv",
  "Echomap Ultra 2 126sv": "/simple-products/echomap-ultra-2-126sv",
  
  // GPSMAP Series
  "GPSMAP 8610xsv": "/simple-products/gpsmap-8610xsv",
  "GPSMAP 8612xsv": "/simple-products/gpsmap-8612xsv", 
  "GPSMAP 943xsv": "/simple-products/gpsmap-943xsv",
  "GPSMAP 1243xsv": "/simple-products/gpsmap-1243xsv",
  "GPSMAP 1042xsv": "/simple-products/gpsmap-1042xsv",
  "GPSMAP 1242xsv": "/simple-products/gpsmap-1242xsv",
  
  // LiveScope Series
  "LiveScope Plus LVS34": "/simple-products/livescope-plus-lvs34",
  "LiveScope XR LVS62": "/simple-products/livescope-xr-lvs62",
  "LiveScope 2 LVS44": "/simple-products/livescope-2-lvs44",
  "LiveScope 2 HD LVS42HD": "/simple-products/livescope-2-lvs42hd",
  "LiveScope 2 HD LVS42HD-IF": "/simple-products/livescope-2-lvs42hd-ice",
  
  // Panoptix Series
  "Panoptix PS22": "/simple-products/panoptix-ps22",
  "Panoptix PS30": "/simple-products/panoptix-ps30-down",
  
  // Transducers
  "Garmin LiveScope Plus LVS34 Transducer Only": "/simple-products/garmin-lvs34",
  "GT54UHD": "/simple-products/gt54uhd-chirp",
  "GT56UHD": "/simple-products/gt56uhd-chirp",
  "GT10HN-IF": "/simple-products/gt10hn-if",
  "GT8HW-IF": "/simple-products/gt8hw-if",
  
  // Batteries
  "30Ah LiFePO4 + 3A Charger": "/simple-products/amped-30ah-lifepo4-with-charger",
  "30Ah LiFePO4 Battery Only": "/simple-products/amped-30ah-lifepo4",
  "32Ah NMC + Charger + BT": "/simple-products/amped-32ah-nmc-with-charger",
  "8Ah LiFePO4 Battery Only": "/simple-products/amped-8ah-lifepo4",
  "8Ah LiFePO4 + 3A Charger": "/simple-products/amped-8ah-lifepo4-with-charger",
  "15Ah LiFePO4 Battery Only": "/simple-products/amped-15ah-lifepo4",
  "15Ah LiFePO4 + 3A Charger": "/simple-products/amped-15ah-lifepo4-with-charger",
  "19Ah NMC + Charger": "/simple-products/amped-19ah-nmc-with-charger",
  "20Ah LiFePO4 Battery Only": "/simple-products/amped-20ah-lifepo4",
  "20Ah LiFePO4 + 3A Charger": "/simple-products/amped-20ah-lifepo4-with-charger",
  "52Ah NMC + Charger + BT": "/simple-products/amped-52ah-nmc-bluetooth-with-charger",
  
  // Shuttles
  "HDI Shuttle Assembled": "/simple-products/hdi-shuttle",
  "HDI Shuttle NON-Assembled": "/simple-products/hdi-shuttle-diy-kit",
  "Summit CNC HD Shuttle": "/simple-products/summit-fishing-shuttle",
  
  // Poles
  "BeatDown BreakAway Transducer Pole - Track Mount - All-Terrain": "/simple-products/breakaway-pole-track-all-terrain",
  "BeatDown BreakAway Transducer Pole - Deck Mount - All-Terrain": "/simple-products/breakaway-pole-flat-all-terrain",
    "BeatDown BreakAway Transducer Pole - Deck Mount - Original": "/simple-products/breakaway-pole-flat-original",

  "BeatDown Fixed Transducer Pole - Deck Mount - All-Terrain": "/simple-products/fixed-pole-flat-all-terrain",
  "BeatDown Fixed Transducer Pole - Deck Mount - Original": "/simple-products/fixed-pole-flat-original",
  
  // HD Innovations Carbon Open Water FFS Pole products
  "HDI Carbon Open Water FFS Pole 18-36 LVS34": "/simple-products/hdi-carbon-18-36-lvs34",
  "HDI Carbon Open Water FFS Pole 18-36 LVS32": "/simple-products/hdi-carbon-18-36-lvs32",
  "HDI Carbon Open Water FFS Pole 24-60 LVS34": "/simple-products/hdi-carbon-24-60-lvs34",
  "HDI Carbon Open Water FFS Pole 24-60 LVS32": "/simple-products/hdi-carbon-24-60-lvs32",
  "HDI Carbon Open Water FFS Pole 32-80 LVS34": "/simple-products/hdi-carbon-32-80-lvs34",
  "HDI Carbon Open Water FFS Pole 32-80 LVS32": "/simple-products/hdi-carbon-32-80-lvs32",
  
  // Bundles
  "Ultra2 106sv + LVS34 Bundle": "/simple-products/Ultra2 106sv + LVS34 Bundle",
  "Ultra2 126sv + LVS34 Bundle": "/simple-products/Ultra2 126sv + LVS34 Bundle"
};

/**
 * Get the price comparison URL for a product based on its CSV lookup ID
 * @param {string} csvLookupId - The CSV lookup ID from product data
 * @returns {string} - The URL path for the product's price comparison page
 */
export const getPriceComparisonUrl = (csvLookupId) => {
  // Handle undefined, null, or empty csvLookupId
  if (!csvLookupId || typeof csvLookupId !== 'string') {
    console.warn('getPriceComparisonUrl called with invalid csvLookupId:', csvLookupId);
    return '/price-comparison'; // Return a safe default URL
  }
  
  // Check if we have a specific mapping for this CSV lookup ID
  if (CSV_LOOKUP_TO_URL_MAPPING[csvLookupId]) {
    return CSV_LOOKUP_TO_URL_MAPPING[csvLookupId];
  }
  
  // If no mapping found, fall back to the generic simple-products route
  // Convert the csvLookupId to a URL-friendly format
  const urlFriendlyId = csvLookupId
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
    
  return `/simple-products/${urlFriendlyId}`;
};

/**
 * Get all available price comparison URLs
 * @returns {Object} - Object mapping csvLookupIds to URLs
 */
export const getAllPriceComparisonUrls = () => {
  return { ...CSV_LOOKUP_TO_URL_MAPPING };
};

/**
 * Reverse map: `/simple-products/:slug` → legacy price lookup string (matches latest_prices_view / csvLookupId).
 * Returns null if the slug is not in {@link CSV_LOOKUP_TO_URL_MAPPING}.
 */
export function getPriceLookupIdForSimpleProductSlug(slug) {
  if (!slug || typeof slug !== 'string') return null;
  const target = `/simple-products/${slug.trim()}`;
  for (const [lookupKey, urlPath] of Object.entries(CSV_LOOKUP_TO_URL_MAPPING)) {
    if (urlPath === target) return lookupKey;
  }
  return null;
}
